import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type MyMemberInfo, memberInfoKey } from '@/hooks/member/memberInfoApi';
import { parseExpiry } from '@/lib/payments/paymentAccount.format';
import type { EditCreditCardInput } from '@/lib/payments/paymentAccount.types';
import { mapToAutoPaySummary, requestEnableMyAutoPay } from '../autoPay/autoPayApi';
import {
  promoteDefault,
  type SavedPaymentMethodsData,
  savedPaymentMethodsKey,
  toBillingAddressInput,
} from '../paymentApi';
import { PaymentEditCardError, requestEditCreditCard } from './creditCardApi';

interface UseEditCreditCardOptions {
  onSuccess?: () => void;
}

/**
 * Edits a saved credit card in place. No verify step — the PAN is immutable and never re-enters
 * the request, so unlike useAddCreditCard there's no PCI payload to sanitize out of error tracking.
 */
export function useEditCreditCard({ onSuccess }: UseEditCreditCardOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: EditCreditCardInput) => {
      const { month: expiryMonth, year: expiryYear } = parseExpiry(input.expiry);
      const updatedCard = await requestEditCreditCard(input.id, {
        expiryMonth,
        expiryYear,
        hsa: input.isHsa,
        isDefault: input.makeDefault,
        nameOnCard: input.billing.name,
        billingAddress: toBillingAddressInput(input.billing),
      });

      // Skip the call entirely when we already know (from the same cache that locks the checkbox)
      // that the member is enrolled — avoids a redundant round trip and Salesforce read. The
      // backend's own idempotent pre-check remains the fallback if this cache is stale.
      const cachedMemberInfo = queryClient.getQueryData<MyMemberInfo>(memberInfoKey);
      const alreadyEnrolled = cachedMemberInfo ? mapToAutoPaySummary(cachedMemberInfo).autoPayEnrolled : false;
      if (input.autoPay && !alreadyEnrolled) {
        // Enrollment failure must not block the card save — swallow and track only.
        try {
          await requestEnableMyAutoPay();
        } catch (err) {
          if (err instanceof Error) {
            trackException(err, { source: 'enableMyAutoPay', context: 'editCreditCard' });
          }
        }
      }

      return updatedCard;
    },
    onSuccess: (updatedCard) => {
      queryClient.setQueryData<SavedPaymentMethodsData>(savedPaymentMethodsKey, (existing) => {
        const withUpdatedCard: SavedPaymentMethodsData = {
          cards: (existing?.cards ?? []).map((card) => (card.id === updatedCard.id ? updatedCard : card)),
          bankAccounts: existing?.bankAccounts ?? [],
        };
        // The edited card's new default (if any) atomically demotes the prior one server-side,
        // across both cards and bank accounts — mirror that here so the optimistic list doesn't
        // show a stale badge anywhere until the invalidate-triggered refetch reconciles it.
        return updatedCard.isDefault ? promoteDefault(withUpdatedCard, updatedCard.id) : withUpdatedCard;
      });
      void queryClient.invalidateQueries({ queryKey: savedPaymentMethodsKey });
      void queryClient.invalidateQueries({ queryKey: memberInfoKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      // PaymentEditCardError wraps editCreditCard's Failure variant (NOT_FOUND, deliberately
      // overloaded — see creditCardApi.ts). No PCI data in the request variables here, so unlike
      // useAddCreditCard there's nothing to sanitize before tracking.
      const code = error instanceof PaymentEditCardError ? error.code : extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'editCreditCard', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return { submit: (input: EditCreditCardInput) => mutation.mutate(input), isPending: mutation.isPending };
}
